/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { Log } from "../../../model/logging";
import { useUnityDispatch, useUnitySelector } from "../../../viewModel";
import { TOAST_AUTO_HIDE_DURATION_MS } from "../../../config";
import {
  hideToastMessage,
  resetToastMessage,
  selectIsToastMessageVisible,
  selectToastMessageText
} from "../../../viewModel/toastMessage";
import ToastMessage from "./ToastMessage";

/**
 * Connected container that reads toast state from Redux, renders
 * `ToastMessage`, and owns the auto-hide timer.
 *
 * Mounted once at the app shell (`src/views/App.tsx`) so any feature can
 * dispatch `showToastMessage` from anywhere in the app.
 */
const ToastMessageContainer: FunctionComponent = (): JSX.Element => {
  const TAG: string = "ToastMessageContainer";
  const dispatch = useUnityDispatch();
  const location = useLocation();

  const isVisible = useUnitySelector(selectIsToastMessageVisible);
  const message = useUnitySelector(selectToastMessageText);

  const hasMountedRef = useRef<boolean>(false);
  const prevLocationRef = useRef<string>(location.pathname);

  // Clear toast message when navigating to a different screen to prevent
  // the message from appearing on the new/previous screen.
  useEffect((): void => {
    if (prevLocationRef.current !== location.pathname) {
      Log.d(
        TAG,
        `Navigation detected — clearing toast message (from=${prevLocationRef.current} to=${location.pathname})`
      );
      dispatch(hideToastMessage());
      dispatch(resetToastMessage());
      prevLocationRef.current = location.pathname;
    }
  }, [location.pathname, dispatch]);

  // Discard any stale message left over from a previous mount so it cannot
  // flash on screen before a real `showToastMessage` dispatch occurs.
  useEffect((): (() => void) => {
    Log.d(TAG, "ToastMessageContainer mounted");
    if (hasMountedRef.current === false) {
      hasMountedRef.current = true;
      if (message !== "") {
        dispatch(resetToastMessage());
      }
    }
    return (): void => {
      Log.d(TAG, "ToastMessageContainer unmounted");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: dispatch is stable, message intentionally excluded
  }, []);

  // Auto-hide after TOAST_AUTO_HIDE_MS. Restarts whenever a new message is
  // shown so rapid, successive restrictions each get their full display time.
  useEffect((): (() => void) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (isVisible === true && message !== "") {
      timeoutId = setTimeout((): void => {
        dispatch(hideToastMessage());
      }, TOAST_AUTO_HIDE_DURATION_MS);
    }

    return (): void => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [isVisible, message, dispatch]);

  return <ToastMessage message={message} isVisible={isVisible} />;
};

export default ToastMessageContainer;
